'use client';

import { useState, useCallback, useEffect } from 'react';
import { ShareNetwork, SpinnerGap, Copy, Check, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

/** A raw tool-output media artifact (no visualization row) — shared via the
 *  shared_artifacts snapshot flow (POST /api/shared-artifacts → /x/:slug). */
export interface ShareMediaItem {
  kind: 'image' | 'video' | 'audio' | 'file' | 'document';
  url: string;
  title?: string;
  mimeType?: string;
  posterUrl?: string;
  subtitle?: string;
  durationSec?: number;
  toolName?: string;
  /** Byte size — forwarded for the `document` kind so /x shows the file size. */
  sizeBytes?: number;
}

interface ShareButtonProps {
  /** Direct visualization UUID — preferred, uses PATCH endpoint → /v/:id */
  visualizationId?: string;
  /** Fallback: session ID for lookup-based visualization sharing */
  sessionId?: string | null;
  /** Fallback: tool type for lookup */
  type?: string;
  /** Fallback: title for lookup */
  title?: string;
  /** Raw media artifact (generated video/image/audio/file) → shared_artifacts. */
  mediaItem?: ShareMediaItem | null;
}

export function ShareButton({ visualizationId, sessionId, type, title, mediaItem }: ShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Media (shared_artifacts) path only: slug for revoke + shared-state indicator.
  const [mediaSlug, setMediaSlug] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  // The media path is used when there's a shareable media item AND no
  // visualization row to prefer. Everything else keeps the /v flow.
  const useMediaFlow = Boolean(mediaItem && !visualizationId);

  // Reflect existing shared state so the button/dialog can show an indicator
  // without a click (media path only — one cheap authed lookup on mount).
  useEffect(() => {
    if (!useMediaFlow || !mediaItem) return;
    let cancelled = false;
    fetch(`/api/shared-artifacts/lookup?url=${encodeURIComponent(mediaItem.url)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.shared) return;
        setMediaSlug(data.slug);
        setShareUrl(`${window.location.origin}${data.path}`);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [useMediaFlow, mediaItem]);

  const shareMedia = useCallback(async () => {
    if (!mediaItem) return;
    setLoading(true);
    setError(null);
    setOpen(true);
    try {
      const res = await fetch('/api/shared-artifacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: mediaItem.kind,
          title: mediaItem.title ?? title,
          sessionId: sessionId ?? undefined,
          payload: {
            url: mediaItem.url,
            mimeType: mediaItem.mimeType,
            posterUrl: mediaItem.posterUrl,
            subtitle: mediaItem.subtitle,
            durationSec: mediaItem.durationSec,
            toolName: mediaItem.toolName,
            sizeBytes: mediaItem.sizeBytes,
          },
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to create share link');
        return;
      }
      const data = await res.json();
      setMediaSlug(data.slug);
      setShareUrl(`${window.location.origin}${data.path}`);
    } catch {
      setError('Failed to create share link');
    } finally {
      setLoading(false);
    }
  }, [mediaItem, title, sessionId]);

  const revokeMedia = useCallback(async () => {
    if (!mediaSlug) return;
    setRevoking(true);
    setError(null);
    try {
      const res = await fetch(`/api/shared-artifacts/${encodeURIComponent(mediaSlug)}`, {
        method: 'DELETE',
      });
      if (!res.ok && res.status !== 404) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to turn off sharing');
        return;
      }
      setMediaSlug(null);
      setShareUrl(null);
    } catch {
      setError('Failed to turn off sharing');
    } finally {
      setRevoking(false);
    }
  }, [mediaSlug]);

  const handleShare = useCallback(async () => {
    if (useMediaFlow) {
      if (shareUrl) {
        setOpen(true);
        return;
      }
      await shareMedia();
      return;
    }

    // Need either a direct UUID or session-based lookup params
    if (!visualizationId && !sessionId) return;

    // If already shared, just open the modal
    if (shareUrl) {
      setOpen(true);
      return;
    }

    setLoading(true);
    setError(null);
    setOpen(true);

    try {
      let vizId = visualizationId;

      if (vizId) {
        // Direct UUID: use PATCH to set public
        const res = await fetch(`/api/visualizations/${vizId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isPublic: true }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || 'Failed to create share link');
          setLoading(false);
          return;
        }
      } else {
        // Fallback: lookup by session+type+title
        const res = await fetch('/api/visualizations/share', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, type, title }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || 'Failed to create share link');
          setLoading(false);
          return;
        }
        const data = await res.json();
        vizId = data.id;
      }

      const url = `${window.location.origin}/v/${vizId}`;
      setShareUrl(url);
    } catch {
      setError('Failed to create share link');
    } finally {
      setLoading(false);
    }
  }, [useMediaFlow, shareMedia, visualizationId, sessionId, type, title, shareUrl]);

  const [copyError, setCopyError] = useState<string | null>(null);
  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError('Copy blocked — select the URL and copy manually.');
      window.setTimeout(() => setCopyError(null), 4000);
    }
  }, [shareUrl]);

  if (!visualizationId && !sessionId && !useMediaFlow) return null;

  const isShared = Boolean(shareUrl);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleShare}
        className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        {isShared ? (
          <Check className="size-3.5 text-green-500" weight="bold" />
        ) : (
          <ShareNetwork className="size-3.5" />
        )}
        {isShared ? 'Shared' : 'Share'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle>{useMediaFlow ? 'Share artifact' : 'Share Visualization'}</DialogTitle>
            <DialogDescription>
              Anyone with the link can view this {useMediaFlow ? 'artifact' : 'visualization'}.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div
              className="flex items-center justify-center py-8"
              role="status"
              aria-live="polite"
            >
              <SpinnerGap className="size-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="ml-2 text-sm text-muted-foreground">Creating share link...</span>
            </div>
          ) : error ? (
            <div className="py-4" role="alert" aria-live="assertive" aria-atomic="true">
              <p className="text-sm text-destructive">{error}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setError(null);
                  setShareUrl(null);
                  handleShare();
                }}
                className="mt-3"
              >
                Try again
              </Button>
            </div>
          ) : shareUrl ? (
            <div className="overflow-hidden">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  aria-label="Share URL"
                  className="min-w-0 flex-1 rounded-md border bg-muted/50 px-3 py-2 text-sm outline-none"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  aria-label={copied ? 'Copied to clipboard' : 'Copy share URL'}
                  className="shrink-0 gap-1.5"
                >
                  {copied ? (
                    <>
                      <Check className="size-4 text-green-500" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="size-4" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              {/* Live regions: announce copy success/failure to screen readers
                  without taking visual focus from the button. */}
              <div className="sr-only" role="status" aria-live="polite">
                {copied ? 'Share URL copied to clipboard.' : ''}
              </div>
              {copyError && (
                <p
                  className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-300/85"
                  role="alert"
                  aria-live="assertive"
                >
                  <WarningCircle weight="duotone" className="size-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{copyError}</span>
                </p>
              )}
              {/* Revoke (media path only) — immediate: the slug 404s next load. */}
              {useMediaFlow && mediaSlug && (
                <div className="mt-3 flex items-center justify-between border-t pt-3">
                  <span className="text-xs text-muted-foreground">This artifact is public.</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={revokeMedia}
                    disabled={revoking}
                    className="h-7 gap-1.5 text-xs text-destructive hover:text-destructive"
                  >
                    {revoking ? <SpinnerGap className="size-3.5 animate-spin" /> : null}
                    Stop sharing
                  </Button>
                </div>
              )}
            </div>
          ) : useMediaFlow ? (
            // Media path, not yet shared: an explicit enable button (matches
            // the session-share "make public" language).
            <div className="py-2">
              <Button type="button" onClick={shareMedia} className="w-full gap-1.5">
                <ShareNetwork className="size-4" />
                Create public link
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
